import { apiClient } from './client';

export interface User {
  id: string;
  email: string;
  name: string;
  role: string;
  isActive: boolean;
  lastLoginAt: string | null;
  createdAt: string;
}

export interface UserInvite {
  id: string;
  email: string;
  role: string;
  expiresAt: string;
  inviteUrl?: string;
  inviter?: {
    id: string;
    name: string;
    email: string;
  };
}

export interface AuditLog {
  id: string;
  userId: string | null;
  action: string;
  resourceType: string;
  resourceId: string | null;
  details: Record<string, unknown>;
  ipAddress: string | null;
  userAgent: string | null;
  createdAt: string;
  user?: {
    id: string;
    name: string;
    email: string;
  };
}

export interface PaginationResult {
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
}

export interface UsersResponse {
  users: User[];
  pagination: PaginationResult;
}

export interface AuditLogsResponse {
  logs: AuditLog[];
  pagination: PaginationResult;
}

export async function getUsers(params?: {
  page?: number;
  pageSize?: number;
  search?: string;
  role?: string;
  isActive?: boolean;
}): Promise<UsersResponse> {
  return apiClient.get('/users', { params: params as Record<string, string | number | boolean | undefined> });
}

export async function updateUser(
  id: string,
  data: {
    name?: string;
    role?: 'admin' | 'member' | 'viewer';
    isActive?: boolean;
  }
): Promise<{ user: User }> {
  return apiClient.patch(`/users/${id}`, data);
}

export async function deactivateUser(id: string): Promise<{ user: User }> {
  return apiClient.delete(`/users/${id}`);
}

export async function inviteUser(data: {
  email: string;
  role?: 'admin' | 'member' | 'viewer';
}): Promise<{ invite: UserInvite }> {
  return apiClient.post('/users/invite', data);
}

export async function acceptInvite(data: {
  token: string;
  name: string;
  password: string;
}): Promise<{ user: User }> {
  return apiClient.post('/users/invite/accept', data);
}

export async function getPendingInvites(): Promise<{ invites: UserInvite[] }> {
  return apiClient.get('/users/invites');
}

export async function deleteInvite(id: string): Promise<{ success: boolean }> {
  return apiClient.delete(`/users/invites/${id}`);
}

export async function resendInvite(id: string): Promise<{ success: boolean; invite: UserInvite }> {
  return apiClient.post(`/users/invites/${id}/resend`);
}

export type AccessOverride = 'allow' | 'deny';

export interface UserAccessResponse {
  overrides: Record<string, AccessOverride>;
  teamIds: string[];
  effective: string[];
}

export async function getUserAccess(id: string): Promise<UserAccessResponse> {
  return apiClient.get(`/users/${id}/access`);
}

export async function updateUserAccess(
  id: string,
  overrides: Record<string, AccessOverride>,
  teamIds?: string[]
): Promise<UserAccessResponse> {
  return apiClient.patch(`/users/${id}/access`, { overrides, teamIds });
}

export type RoleDefaults = Record<string, Record<string, boolean>>;

export async function getRoleDefaults(): Promise<{ defaults: RoleDefaults }> {
  return apiClient.get('/users/role-defaults');
}

export async function updateRoleDefaults(
  entries: Array<{ role: 'admin' | 'member' | 'viewer'; feature: string; allowed: boolean }>
): Promise<{ defaults: RoleDefaults }> {
  return apiClient.patch('/users/role-defaults', { entries });
}

export async function getAuditLogs(params?: {
  page?: number;
  pageSize?: number;
  action?: string;
  resourceType?: string;
  userId?: string;
}): Promise<AuditLogsResponse> {
  return apiClient.get('/users/audit-log', { params: params as Record<string, string | number | boolean | undefined> });
}
